import ADIShopMyBranchLeftNav from "@NA/Components/ShopMyBranch/ADIShopMyBranchLeftNav";
import { getDCLeftNavData, DCLeftNavModal, NavSubLevelModal } from "@NA/Services/ADIShopMyBranchService";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link from "@insite/mobius/Link";
import { UnorderedList } from "@insite/mobius/Lists";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import ListItem from "@insite/mobius/Lists/ListItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";
import Hidden from "@insite/mobius/Hidden";
import Button from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ADILeftNav from "./ADILeftNav";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

export interface ShopMyBranchDCLeftNavStyles {
    leftNavContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    listStyle?: InjectableCss;
    mobileListStyle?: InjectableCss;
    innerUnorderedListStyle?: InjectableCss;
    innerListItemStyle?: InjectableCss;
    mobileContainer?: InjectableCss;
    toggleOptionColor?: InjectableCss;
}

const defaultDCData: DCLeftNavModal = {
    attributeId: "",
    dc: "",
    brand: [],
    category: [],
    mwts: [],
};

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state),
    breadcrumbLinks: state.components.breadcrumbs.links,
});

type Props = ReturnType<typeof mapStateToProps>;

const ADIDCLeftNav = ({ fields, currentPage, breadcrumbLinks }: any) => {
    const [dcLeftNavData, setDcLeftNavData] = useState<DCLeftNavModal>(defaultDCData);
    const [expandAllOptions, setExpandAllOptions] = useState<boolean>(true);
    const [isMobile, setIsMobile] = useState(false);

    const shopMyBranchDCLeftNavStyles: ShopMyBranchDCLeftNavStyles = {
        leftNavContainer: {
            css: css`
                min-width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 30px 0px 10px 0;
                        `,
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 30px 0px 10px 0;
                        `,
                        css`
                            max-width: 100%;
                            width: 100% !important;
                            padding: 30px 0 10px 0;
                        `,
                        css`
                            max-width: 250px;
                            width: 100% !important;
                            padding: 0 0px 0 0;
                            min-width: 250px;
                        `,
                        css`
                            max-width: 250px;
                            width: 100% !important;
                            padding: 0 0px 0 0;
                            min-width: 250px;
                        `,
                    ])}
                .parent-menu {
                    cursor: pointer;
                    border-top: 1px solid ${ADIColors.gray2}!important;
                    font-size: 15px;
                    font-weight: bold;
                    line-height: normal;
                    letter-spacing: normal;
                    color: ${getColor("text.main")};
                    padding-bottom: 15px;
                    padding-top: 17px;
                }
            `,
        },

        titleText: {
            variant: "h2",
            css: css`
                margin-bottom: 9px;
                font-size: 16px;
                font-weight: bold;
                line-height: normal;
                letter-spacing: normal;
                color: ${ADIColors.darkBlue1};
            `,
        },
        listStyle: {
            css: css`
                > ul {
                    list-style: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    > li:first-child {
                        .parent-menu {
                            border-top: 2px solid #000 !important;
                        }
                    }
                }
            `,
        },
        mobileListStyle: {
            css: css`
                > ul {
                    display: ${expandAllOptions ? "none" : "block"};
                    list-style: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                }
            `,
        },
        innerUnorderedListStyle: {
            css: css`
                max-height: 100%;
                width: 100% !important;
                padding-right: 22px !important;
                overflow: auto !important;
                &::-webkit-scrollbar {
                    width: 5px;
                }
                &::-webkit-scrollbar-thumb {
                    background: ${ADIColors.gray7};
                    border-radius: 10px;
                }
            `,
        },
        innerListItemStyle: {
            css: css`
                margin-bottom: 12px;
                > a {
                    font-size: 14px;
                    font-weight: normal;
                    line-height: normal;
                    letter-spacing: normal;
                    > span {
                        color: ${ADIColors.black} !important;
                    }
                }
            `,
        },
        mobileContainer: {
            css: css`
                display: flex;
                background: ${ADIColors.gray6};
                justify-content: space-between;
                padding: 15px 0;
            `,
        },
        toggleOptionColor: {
            css: css`
                color: ${ADIColors.blue5};
            `,
        },
    };

    const styles = shopMyBranchDCLeftNavStyles;

    const onWindowResize = () => {
        if (window.innerWidth > 1024) {
            setIsMobile(false);
        } else {
            setIsMobile(true);
        }
    };

    useEffect(() => {
        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        fetchData();
        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    const fetchData = async () => {
        if (sessionStorage.getItem("DCLeftNav")) {
            setDcLeftNavData(JSON.parse(sessionStorage.getItem("DCLeftNav")!));
        } else {
            const response = await getDCLeftNavData();
            if ((response && response.category?.length) || response.brand?.length) {
                sessionStorage.setItem("DCLeftNav", JSON.stringify(response));
                setDcLeftNavData(response);
            }
        }
    };

    return (
        <StyledWrapper>
            {!isMobile && (
                <StyledWrapper {...styles.leftNavContainer}>
                    <Typography {...styles.titleText}>{fields.title}</Typography>
                    <StyledWrapper {...styles.listStyle}>
                        <UnorderedList>
                            <ADILeftNav title="Categories" leftNavData={dcLeftNavData.category}></ADILeftNav>
                            <ADILeftNav
                                title="Brands"
                                leftNavData={dcLeftNavData.brand?.splice(0, 15)}
                                showMoreButton={true}
                            ></ADILeftNav>
                        </UnorderedList>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ADIDCLeftNav),
    definition: {
        group: "Basic",
        displayName: "Left Nav Filter",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
